import React, { useState, useEffect } from 'react';
import { ppidContentService } from '../../../services/ppidContentService';
import { kategoriDokumenService } from '../../../services/kategoriDokumenService'; // ✅ service baru
import {
    FaTimes, FaSave, FaUpload, FaFilePdf, FaFileImage,
    FaFileWord, FaFileExcel, FaFileAlt, FaDownload,
    FaEdit, FaTrash, FaPlus, FaEye, FaCheckCircle,
    FaExclamationTriangle, FaArchive, FaCalendarAlt, FaFolder,
    FaPlusCircle
} from 'react-icons/fa';

const PPIDContentForm = ({ menu, onClose }) => {
    const [contents, setContents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [editingContent, setEditingContent] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [notification, setNotification] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState(null);

    const [kategoriDokumenOptions, setKategoriDokumenOptions] = useState([]);
    const [loadingKategori, setLoadingKategori] = useState(false);

    const [showKategoriInput, setShowKategoriInput] = useState(false);
    const [newKategoriKode, setNewKategoriKode] = useState('');
    const [newKategoriNama, setNewKategoriNama] = useState('');
    const [creatingKategori, setCreatingKategori] = useState(false);

    const [formData, setFormData] = useState({
        judul: '',
        deskripsi: '',
        konten: '',
        kategori: '',
        tahun: new Date().getFullYear(),
        kategori_dokumen: '',
        menu_id: menu?.id || '',
        menu_name: menu?.name || '',
        status: 'draft',
    });

    const [fileUpload, setFileUpload] = useState(null);
    const [imageUpload, setImageUpload] = useState(null);

    const loadKategoriDokumen = async () => {
        setLoadingKategori(true);
        const result = await kategoriDokumenService.getAll();
        if (result.success) {
            setKategoriDokumenOptions(result.data || []);
            // Set default ke kategori pertama jika formData.kategori_dokumen kosong
            if (!formData.kategori_dokumen && result.data?.length > 0) {
                setFormData(prev => ({ ...prev, kategori_dokumen: result.data[0].kode }));
            }
        }
        setLoadingKategori(false);
    };

    useEffect(() => {
        loadKategoriDokumen();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (menu) {
            const kategori = menu.slug || menu.name.toLowerCase().replace(/\s+/g, '_');
            setFormData(prev => ({
                ...prev,
                kategori,
                menu_id: menu.id,
                menu_name: menu.name,
            }));
            loadContents();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [menu]);

    const loadContents = async () => {
        setLoading(true);
        const result = await ppidContentService.getContents({ menu_id: menu.id });
        if (result.success) {
            setContents(result.data.data || []);
        }
        setLoading(false);
    };

    const handleCreateKategori = async () => {
        if (!newKategoriKode.trim() || !newKategoriNama.trim()) {
            setNotification({ message: 'Kode dan nama kategori wajib diisi', type: 'error' });
            setTimeout(() => setNotification(null), 3000);
            return;
        }
        setCreatingKategori(true);
        const result = await kategoriDokumenService.create({
            kode: newKategoriKode.trim().toUpperCase(),
            nama: newKategoriNama.trim(),
        });
        if (result.success) {
            setNotification({ message: 'Kategori berhasil ditambahkan', type: 'success' });
            await loadKategoriDokumen();
            // Auto-select kategori baru
            const kode = result.data?.kode || result.data?.data?.kode;
            if (kode) {
                setFormData(prev => ({ ...prev, kategori_dokumen: kode }));
            }
            setNewKategoriKode('');
            setNewKategoriNama('');
            setShowKategoriInput(false);
        } else {
            setNotification({ message: result.message, type: 'error' });
        }
        setCreatingKategori(false);
        setTimeout(() => setNotification(null), 3000);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e) => {
        setFileUpload(e.target.files[0]);
    };

    const handleImageChange = (e) => {
        setImageUpload(e.target.files[0]);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const submitData = new FormData();
        submitData.append('judul', formData.judul);
        submitData.append('deskripsi', formData.deskripsi || '');
        submitData.append('konten', formData.konten || '');
        submitData.append('kategori', formData.kategori);
        submitData.append('tahun', formData.tahun || '');
        submitData.append('kategori_dokumen', formData.kategori_dokumen || '');
        submitData.append('menu_id', formData.menu_id);
        submitData.append('menu_name', formData.menu_name);
        submitData.append('status', formData.status);

        if (fileUpload) submitData.append('file', fileUpload);
        if (imageUpload) submitData.append('image', imageUpload);

        setSubmitting(true);
        let result;
        if (editingContent) {
            result = await ppidContentService.updateContent(editingContent.id, submitData);
        } else {
            result = await ppidContentService.createContent(submitData);
        }

        if (result.success) {
            setNotification({ message: result.message, type: 'success' });
            setShowForm(false);
            setEditingContent(null);
            resetForm();
            loadContents();
        } else {
            setNotification({ message: result.message, type: 'error' });
        }
        setSubmitting(false);
        setTimeout(() => setNotification(null), 3000);
    };

    const resetForm = () => {
        setFormData({
            judul: '',
            deskripsi: '',
            konten: '',
            kategori: menu.slug,
            tahun: new Date().getFullYear(),
            kategori_dokumen: kategoriDokumenOptions[0]?.kode || '',
            menu_id: menu.id,
            menu_name: menu.name,
            status: 'draft',
        });
        setFileUpload(null);
        setImageUpload(null);
        setShowKategoriInput(false);
        setNewKategoriKode('');
        setNewKategoriNama('');
    };

    const handleEdit = (content) => {
        setEditingContent(content);
        setFormData({
            judul: content.judul,
            deskripsi: content.deskripsi || '',
            konten: content.konten || '',
            kategori: content.kategori,
            tahun: content.tahun || new Date().getFullYear(),
            kategori_dokumen: content.kategori_dokumen || (kategoriDokumenOptions[0]?.kode || ''),
            menu_id: content.menu_id,
            menu_name: content.menu_name,
            status: content.status,
        });
        setShowForm(true);
    };

    const handleDelete = async (id) => {
        const result = await ppidContentService.deleteContent(id);
        if (result.success) {
            setNotification({ message: result.message, type: 'success' });
            loadContents();
        } else {
            setNotification({ message: result.message, type: 'error' });
        }
        setDeleteConfirm(null);
    };

    const handleStatusChange = async (id, status) => {
        const result = await ppidContentService.updateStatus(id, status);
        if (result.success) {
            setNotification({ message: result.message, type: 'success' });
            loadContents();
        }
    };

    const getFileIcon = (fileType) => {
        switch (fileType?.toLowerCase()) {
            case 'pdf': return <FaFilePdf className="text-red-500" />;
            case 'jpg': case 'jpeg': case 'png': return <FaFileImage className="text-blue-500" />;
            case 'doc': case 'docx': return <FaFileWord className="text-blue-700" />;
            case 'xls': case 'xlsx': return <FaFileExcel className="text-green-600" />;
            default: return <FaFileAlt className="text-gray-500" />;
        }
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return '-';
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('id-ID', {
            day: 'numeric', month: 'long', year: 'numeric'
        });
    };

    return (
        <div className="fixed inset-0 bg-black/70 z-[999] flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="bg-gradient-to-r from-[#0F4C3A] to-[#2E7D32] p-6 text-white sticky top-0 z-10">
                    <div className="flex justify-between items-center">
                        <div>
                            <h3 className="text-xl font-bold">Kelola Konten PPID</h3>
                            <p className="text-sm opacity-80">Menu: {menu?.name}</p>
                        </div>
                        <button onClick={onClose} className="text-white/80 hover:text-white">
                            <FaTimes size={24} />
                        </button>
                    </div>
                </div>

                <div className="p-6">
                    {/* Notification */}
                    {notification && (
                        <div className={`mb-4 p-3 rounded-lg flex items-center ${notification.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                            }`}>
                            {notification.type === 'success' ? <FaCheckCircle className="mr-2" /> : <FaExclamationTriangle className="mr-2" />}
                            {notification.message}
                        </div>
                    )}

                    {/* Header List */}
                    <div className="flex justify-between items-center mb-6">
                        <h4 className="font-semibold text-[#0F4C3A]">Daftar Konten</h4>
                        <button
                            onClick={() => { setShowForm(true); setEditingContent(null); resetForm(); }}
                            className="btn-primary text-sm flex items-center"
                        >
                            <FaPlus className="mr-1" /> Tambah Konten
                        </button>
                    </div>

                    {/* ============ FORM INPUT ============ */}
                    {showForm && (
                        <div className="bg-gray-50 rounded-lg p-4 mb-6 border">
                            <h5 className="font-semibold mb-4">
                                {editingContent ? 'Edit Konten' : 'Tambah Konten Baru'}
                            </h5>
                            <form onSubmit={handleSubmit} className="space-y-4">

                                {/* Judul */}
                                <div>
                                    <label className="block text-sm font-medium mb-1">Judul *</label>
                                    <input
                                        type="text"
                                        name="judul"
                                        value={formData.judul}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                        required
                                    />
                                </div>

                                {/* Deskripsi */}
                                <div>
                                    <label className="block text-sm font-medium mb-1">Deskripsi</label>
                                    <textarea
                                        name="deskripsi"
                                        value={formData.deskripsi}
                                        onChange={handleInputChange}
                                        rows="2"
                                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                    />
                                </div>

                                {/* Konten */}
                                <div>
                                    <label className="block text-sm font-medium mb-1">Konten</label>
                                    <textarea
                                        name="konten"
                                        value={formData.konten}
                                        onChange={handleInputChange}
                                        rows="4"
                                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                    />
                                </div>

                                {/* Tahun & Kategori Dokumen */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium mb-1">
                                            <FaCalendarAlt className="inline mr-1 text-[#2E7D32]" />
                                            Tahun *
                                        </label>
                                        <select
                                            name="tahun"
                                            value={formData.tahun}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                            required
                                        >
                                            {Array.from({ length: 10 }, (_, i) => {
                                                const year = new Date().getFullYear() + 2 - i;
                                                return <option key={year} value={year}>{year}</option>;
                                            })}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-1">
                                            <FaFolder className="inline mr-1 text-[#2E7D32]" />
                                            Kategori Dokumen *
                                        </label>

                                        {loadingKategori ? (
                                            <div className="text-sm text-gray-400 py-2">Memuat kategori...</div>
                                        ) : (
                                            <select
                                                name="kategori_dokumen"
                                                value={formData.kategori_dokumen}
                                                onChange={handleInputChange}
                                                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                                required
                                            >
                                                <option value="">-- Pilih Kategori --</option>
                                                {kategoriDokumenOptions.map(opt => (
                                                    <option key={opt.kode} value={opt.kode}>
                                                        {opt.kode} - {opt.nama}
                                                    </option>
                                                ))}
                                            </select>
                                        )}

                                        {/* Tombol / Form tambah kategori baru */}
                                        {!showKategoriInput ? (
                                            <button
                                                type="button"
                                                onClick={() => setShowKategoriInput(true)}
                                                className="mt-1 text-xs text-[#2E7D32] hover:text-[#4CAF50] flex items-center gap-1"
                                            >
                                                <FaPlusCircle size={10} /> Tambah Kategori Baru
                                            </button>
                                        ) : (
                                            <div className="mt-2 p-2 bg-white border border-gray-200 rounded-lg space-y-2">
                                                <input
                                                    type="text"
                                                    value={newKategoriKode}
                                                    onChange={(e) => setNewKategoriKode(e.target.value)}
                                                    placeholder="Kode (mis. RPJMD)"
                                                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:ring-2 focus:ring-[#2E7D32]"
                                                    maxLength={20}
                                                />
                                                <input
                                                    type="text"
                                                    value={newKategoriNama}
                                                    onChange={(e) => setNewKategoriNama(e.target.value)}
                                                    placeholder="Nama lengkap kategori"
                                                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:ring-2 focus:ring-[#2E7D32]"
                                                />
                                                <div className="flex gap-1">
                                                    <button
                                                        type="button"
                                                        onClick={handleCreateKategori}
                                                        disabled={creatingKategori}
                                                        className="flex-1 bg-[#2E7D32] hover:bg-[#4CAF50] text-white px-2 py-1.5 rounded text-xs flex items-center justify-center gap-1 disabled:opacity-50"
                                                    >
                                                        {creatingKategori ? (
                                                            <>
                                                                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                                                                Menyimpan...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <FaSave size={10} /> Simpan
                                                            </>
                                                        )}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setShowKategoriInput(false);
                                                            setNewKategoriKode('');
                                                            setNewKategoriNama('');
                                                        }}
                                                        className="px-2 py-1.5 text-gray-500 hover:text-gray-700 text-xs"
                                                    >
                                                        <FaTimes />
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Kategori & Status */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Kategori Menu</label>
                                        <input
                                            type="text"
                                            name="kategori"
                                            value={formData.kategori}
                                            readOnly
                                            className="w-full px-4 py-2 border rounded-lg bg-gray-100"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Status</label>
                                        <select
                                            name="status"
                                            value={formData.status}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                        >
                                            <option value="draft">Draft</option>
                                            <option value="publish">Publish</option>
                                            <option value="arsip">Arsip</option>
                                        </select>
                                    </div>
                                </div>

                                {/* Upload File & Image */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium mb-1">
                                            <FaUpload className="inline mr-1" /> File Upload
                                        </label>
                                        <input
                                            type="file"
                                            onChange={handleFileChange}
                                            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                                            className="w-full px-4 py-2 border rounded-lg"
                                        />
                                        {editingContent?.file_name && !fileUpload && (
                                            <p className="text-xs text-gray-500 mt-1">
                                                File saat ini: {editingContent.file_name}
                                            </p>
                                        )}
                                        {fileUpload && (
                                            <p className="text-xs text-green-600 mt-1">✓ {fileUpload.name}</p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-1">
                                            <FaUpload className="inline mr-1" /> Gambar
                                        </label>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleImageChange}
                                            className="w-full px-4 py-2 border rounded-lg"
                                        />
                                        {imageUpload && (
                                            <p className="text-xs text-green-600 mt-1">✓ {imageUpload.name}</p>
                                        )}
                                    </div>
                                </div>

                                {/* Buttons */}
                                <div className="flex justify-end space-x-3 pt-4 border-t">
                                    <button
                                        type="button"
                                        onClick={() => setShowForm(false)}
                                        className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                                    >
                                        Batal
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        className="flex items-center bg-[#2E7D32] text-white px-6 py-2 rounded-lg hover:bg-[#1B5E20] disabled:opacity-50"
                                    >
                                        {submitting ? 'Menyimpan...' : <><FaSave className="mr-1" /> Simpan</>}
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    {/* ============ DAFTAR KONTEN (GROUP BY TAHUN & KATEGORI) ============ */}
                    <div className="space-y-3">
                        {loading ? (
                            <div className="text-center py-8">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2E7D32] mx-auto"></div>
                            </div>
                        ) : contents.length === 0 ? (
                            <div className="text-center py-8 text-gray-500">
                                Belum ada konten untuk menu ini
                            </div>
                        ) : (
                            Object.entries(
                                contents.reduce((acc, item) => {
                                    const tahun = item.tahun || 'Tidak Ada Tahun';
                                    const kategoriDok = item.kategori_dokumen || 'LAINNYA';
                                    if (!acc[tahun]) acc[tahun] = {};
                                    if (!acc[tahun][kategoriDok]) acc[tahun][kategoriDok] = [];
                                    acc[tahun][kategoriDok].push(item);
                                    return acc;
                                }, {})
                            )
                                .sort(([a], [b]) => b.localeCompare(a))
                                .map(([tahun, kategoriGroup]) => (
                                    <div key={tahun} className="mb-6">
                                        <div className="flex items-center gap-2 mb-3 pb-2 border-b-2 border-[#2E7D32]">
                                            <FaCalendarAlt className="text-[#2E7D32]" />
                                            <h3 className="text-lg font-bold text-[#0F4C3A]">
                                                Tahun {tahun}
                                            </h3>
                                        </div>

                                        {Object.entries(kategoriGroup).map(([kategoriDok, items]) => (
                                            <div key={kategoriDok} className="mb-5 ml-4">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <FaFolder className="text-[#2E7D32]" size={14} />
                                                    <h4 className="text-base font-bold text-gray-700">
                                                        {kategoriDok}
                                                    </h4>
                                                    <span className="text-xs text-gray-400">
                                                        ({items.length} dokumen)
                                                    </span>
                                                </div>

                                                <div className="space-y-2 ml-6">
                                                    {items.map((content) => (
                                                        <div
                                                            key={content.id}
                                                            className="bg-white border rounded-lg p-3 hover:shadow-md transition-shadow"
                                                        >
                                                            <div className="flex items-start justify-between">
                                                                <div className="flex items-start flex-1">
                                                                    <div className="mr-3 mt-1">
                                                                        {getFileIcon(content.file_type)}
                                                                    </div>
                                                                    <div className="flex-1">
                                                                        <h5 className="font-semibold text-gray-800 text-sm">
                                                                            {content.judul}
                                                                        </h5>
                                                                        {content.deskripsi && (
                                                                            <p className="text-xs text-gray-500 mt-1">
                                                                                {content.deskripsi}
                                                                            </p>
                                                                        )}
                                                                        <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-gray-400">
                                                                            <span className="flex items-center">
                                                                                <FaCalendarAlt className="mr-1" size={10} />
                                                                                {formatDate(content.created_at)}
                                                                            </span>
                                                                            {content.file_name && (
                                                                                <span>
                                                                                    {content.file_name} ({formatFileSize(content.file_size)})
                                                                                </span>
                                                                            )}
                                                                            <span>👁️ {content.views}</span>
                                                                            <span>⬇️ {content.downloads}</span>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div className="flex items-center space-x-1 ml-2">
                                                                    <span className={`px-2 py-0.5 rounded-full text-[10px] ${content.status === 'publish' ? 'bg-green-100 text-green-700' :
                                                                        content.status === 'arsip' ? 'bg-gray-100 text-gray-500' :
                                                                            'bg-yellow-100 text-yellow-700'
                                                                        }`}>
                                                                        {content.status}
                                                                    </span>
                                                                    <button
                                                                        onClick={() => handleEdit(content)}
                                                                        className="p-1 text-blue-500 hover:bg-blue-50 rounded"
                                                                        title="Edit"
                                                                    >
                                                                        <FaEdit size={12} />
                                                                    </button>
                                                                    {content.status !== 'publish' && (
                                                                        <button
                                                                            onClick={() => handleStatusChange(content.id, 'publish')}
                                                                            className="p-1 text-green-500 hover:bg-green-50 rounded"
                                                                            title="Publish"
                                                                        >
                                                                            <FaEye size={12} />
                                                                        </button>
                                                                    )}
                                                                    {content.status === 'publish' && (
                                                                        <button
                                                                            onClick={() => handleStatusChange(content.id, 'arsip')}
                                                                            className="p-1 text-gray-500 hover:bg-gray-50 rounded"
                                                                            title="Arsipkan"
                                                                        >
                                                                            <FaArchive size={12} />
                                                                        </button>
                                                                    )}
                                                                    {content.file_path && (
                                                                        <a
                                                                            href={ppidContentService.getDownloadUrl(content.id)}
                                                                            className="p-1 text-purple-500 hover:bg-purple-50 rounded"
                                                                            title="Download"
                                                                        >
                                                                            <FaDownload size={12} />
                                                                        </a>
                                                                    )}
                                                                    <button
                                                                        onClick={() => setDeleteConfirm(content)}
                                                                        className="p-1 text-red-500 hover:bg-red-50 rounded"
                                                                        title="Hapus"
                                                                    >
                                                                        <FaTrash size={12} />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ))
                        )}
                    </div>
                </div>
            </div>

            {/* Delete Confirmation */}
            {deleteConfirm && (
                <div className="fixed inset-0 bg-black/50 z-[1000] flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl max-w-sm w-full p-6 text-center">
                        <p className="mb-4">Hapus konten "{deleteConfirm.judul}"?</p>
                        <div className="flex justify-center gap-3">
                            <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 border rounded-lg">Batal</button>
                            <button onClick={() => handleDelete(deleteConfirm.id)} className="px-4 py-2 bg-red-500 text-white rounded-lg">Hapus</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PPIDContentForm;